/**
 * Draining every match, with no screen open.
 *
 * The outbox is per match, and used to be created by whichever scoring screen was showing
 * that match — so a queue drained only while somebody was looking at it. A scorer who lost
 * signal, finished the innings, closed the app and reopened it on the home screen had balls
 * that nothing would send until they opened that match again.
 *
 * This owns one `Outbox` per match for the lifetime of the page (or of a service worker
 * wake-up), asks the store what is waiting, and drains all of it. A screen that cares about
 * one match `watch`es it instead of building its own outbox, so there is exactly one drainer
 * per match per context and the pill on the scoring screen reports what the background
 * drain is doing rather than a second, parallel opinion.
 *
 * Like the outbox, free of browser APIs: the lock, the transport, the store and the timers
 * all come in through the options.
 */
import type { EventAck, Event, MatchId, SeqGap } from "@sportsos/engine";
import { Outbox } from "./outbox.ts";
import type { OutboxEvents, OutboxOptions, SyncState } from "./outbox.ts";

export type DrainerOptions = Omit<
  OutboxOptions,
  "matchId" | "onStateChange" | "onGaps" | "onStuck"
>;

export interface MatchSync {
  readonly matchId: MatchId;
  readonly state: SyncState;
}

/** What the app shows outside any one match. */
export interface DrainSummary {
  /** Events waiting to be acked, across every match this drainer has looked at. */
  readonly pending: number;
  /** At least one match is blocked on a lapsed session. */
  readonly needsSignIn: boolean;
  /** Events held back because another account wrote them. */
  readonly foreign: number;
}

interface Stuck {
  readonly event: Event;
  readonly ack: EventAck;
}

export class Drainer {
  private readonly options: DrainerOptions;
  private readonly outboxes = new Map<MatchId, Outbox>();
  private readonly states = new Map<MatchId, SyncState>();
  private readonly watchers = new Map<MatchId, Set<OutboxEvents>>();
  private readonly gaps = new Map<MatchId, SeqGap[]>();
  private readonly stuck = new Map<MatchId, Stuck[]>();
  private readonly listeners = new Set<(summary: DrainSummary) => void>();

  constructor(options: DrainerOptions) {
    this.options = options;
  }

  /** The one outbox for a match, created on first use. */
  outbox(matchId: MatchId): Outbox {
    const existing = this.outboxes.get(matchId);
    if (existing !== undefined) return existing;

    const created = new Outbox({
      ...this.options,
      matchId,
      onStateChange: (state) => {
        this.states.set(matchId, state);
        this.each(matchId, (w) => w.onStateChange?.(state));
        const summary = this.summary();
        for (const listener of this.listeners) listener(summary);
      },
      onGaps: (reported) => {
        // Accumulated, so a hole reported once stays on screen after later clean responses
        // (PRD §7.2), including for a screen that mounts after it was reported.
        const known = this.gaps.get(matchId) ?? [];
        const key = (g: SeqGap): string => `${g.inningsNo}:${g.fromSeq}-${g.toSeq}`;
        const seen = new Set(known.map(key));
        const fresh = reported.filter((g) => !seen.has(key(g)));
        if (fresh.length === 0) return;
        this.gaps.set(matchId, [...known, ...fresh]);
        this.each(matchId, (w) => w.onGaps?.(fresh));
      },
      onStuck: (event, ack) => {
        const known = this.stuck.get(matchId) ?? [];
        if (known.some((entry) => entry.event.eventId === event.eventId)) return;
        this.stuck.set(matchId, [...known, { event, ack }]);
        this.each(matchId, (w) => w.onStuck?.(event, ack));
      },
    });
    this.outboxes.set(matchId, created);
    return created;
  }

  /**
   * Hear about one match. Returns the way to stop.
   *
   * What has already happened is replayed, so a screen that mounts after a background drain
   * still shows the current state, and any gap or stuck ball reported earlier.
   */
  watch(matchId: MatchId, handlers: OutboxEvents): () => void {
    const set = this.watchers.get(matchId) ?? new Set<OutboxEvents>();
    set.add(handlers);
    this.watchers.set(matchId, set);

    const state = this.states.get(matchId);
    if (state !== undefined) handlers.onStateChange?.(state);
    const gaps = this.gaps.get(matchId);
    if (gaps !== undefined && gaps.length > 0) handlers.onGaps?.(gaps);
    for (const entry of this.stuck.get(matchId) ?? []) handlers.onStuck?.(entry.event, entry.ack);

    return () => {
      set.delete(handlers);
    };
  }

  /** Hear about the totals. Returns the way to stop. */
  subscribe(listener: (summary: DrainSummary) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  summary(): DrainSummary {
    let pending = 0;
    let foreign = 0;
    let needsSignIn = false;
    for (const state of this.states.values()) {
      if ("pending" in state) pending += state.pending;
      if (state.kind === "signin") needsSignIn = true;
      if (state.kind === "foreign") foreign += state.foreign;
    }
    return { pending, needsSignIn, foreign };
  }

  /**
   * Drain every match that has something waiting. Never rejects: what went wrong is in each
   * match's state, and a caller that only wants the queue emptied has no use for an exception.
   */
  async drainAll(): Promise<readonly MatchSync[]> {
    const waiting = await this.options.store.matchesWithPending();
    return Promise.all(
      waiting.map(async (matchId) => {
        const outbox = this.outbox(matchId);
        await outbox.drain().catch(() => undefined);
        return { matchId, state: outbox.current() };
      }),
    );
  }

  /**
   * The network is back, or the scorer just signed in: forget every backoff and every 401
   * block, and drain everything.
   */
  async onNetworkOnline(): Promise<readonly MatchSync[]> {
    const waiting = await this.options.store.matchesWithPending();
    return Promise.all(
      waiting.map(async (matchId) => {
        const outbox = this.outbox(matchId);
        await outbox.onNetworkOnline().catch(() => undefined);
        return { matchId, state: outbox.current() };
      }),
    );
  }

  /**
   * Delete the unsent balls another account wrote, in every match. Returns how many.
   *
   * Only ever on the scorer's explicit, confirmed choice; a drain never calls it.
   */
  async discardForeign(): Promise<number> {
    const waiting = await this.options.store.matchesWithPending();
    const counts = await Promise.all(waiting.map((id) => this.outbox(id).discardForeign()));
    return counts.reduce((sum, n) => sum + n, 0);
  }

  private each(matchId: MatchId, call: (handlers: OutboxEvents) => void): void {
    for (const handlers of this.watchers.get(matchId) ?? []) call(handlers);
  }
}
